import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardTitle,
  CardContent,
  CardFooter,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useStytch, useStytchUser } from "@stytch/react";
import Loading from "@/components/Loading";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { SETTINGS } from "@/lib/links";

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, {
      message: "Current password is required",
    }),
    newPassword: z
      .string()
      .min(8, { message: "Password must be at least 8 characters long" })
      .regex(/[A-Z]/, {
        message: "Password must contain at least one uppercase letter",
      })
      .regex(/[a-z]/, {
        message: "Password must contain at least one lowercase letter",
      })
      .regex(/[0-9]/, { message: "Password must contain at least one number" }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

function RequiredLabel() {
  return (
    <span
      className="px-2 py-2 rounded-lg text-form-subtitle text-muted-foreground bg-secondary"
      aria-hidden="true"
    >
      Required
    </span>
  );
}

function ProfileLabel({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <FormLabel
      htmlFor={htmlFor}
      className="flex flex-row items-center gap-2 mb-2 font-medium leading-7 text-form-title"
    >
      {children}
    </FormLabel>
  );
}

function ProfileInput({
  id,
  type,
  placeholder,
  ...field
}: {
  id: string;
  type: string;
  placeholder?: string;
}) {
  return (
    <Input
      className="bg-card"
      id={id}
      type={type}
      placeholder={placeholder}
      {...field}
    />
  );
}

export default function ChangePassword() {
  const stytchClient = useStytch();
  const { user: _user, fromCache } = useStytchUser();
  const { t } = useTranslation("common");

  const form = useForm<z.infer<typeof changePasswordSchema>>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Change password mutation using Stytch's reset by existing password
  const changePasswordMutation = useMutation({
    mutationFn: async (data: z.infer<typeof changePasswordSchema>) => {
      // Use Stytch's password reset by existing password flow
      const response = await stytchClient.passwords.resetByExistingPassword({
        email: _user?.emails[0]?.email || "",
        existing_password: data.currentPassword,
        new_password: data.newPassword,
        session_duration_minutes: 60 * 24 * 7, // 7 days
      });

      if (response.status_code !== 200) {
        throw new Error("Failed to change password");
      }

      return response;
    },
    onSuccess: () => {
      toast({
        title: t("toast.password.success.title"),
        description: t("toast.password.success.description"),
      });
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: t("toast.password.error.title"),
        description: error.message || t("toast.password.error.description"),
      });
    },
  });

  const onSubmit = async (data: z.infer<typeof changePasswordSchema>) => {
    changePasswordMutation.mutate(data);
  };

  if (fromCache) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col h-full px-6 bg-background">
      <div className="mb-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to={SETTINGS} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Settings
          </Link>
        </Button>
      </div>

      <Card className="bg-transparent border-none">
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>
            Update your password to keep your account secure. You'll need to
            enter your current password to confirm the change.
          </CardDescription>
        </CardHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent className="flex flex-col gap-4">
              <FormField
                name="currentPassword"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <ProfileLabel htmlFor="currentPassword">
                      Current Password
                      <RequiredLabel />
                    </ProfileLabel>
                    <FormControl>
                      <ProfileInput
                        id="currentPassword"
                        type="password"
                        placeholder="Enter your current password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>
                      {form.formState.errors.currentPassword?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                name="newPassword"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <ProfileLabel htmlFor="newPassword">
                      New Password
                      <RequiredLabel />
                    </ProfileLabel>
                    <FormControl>
                      <ProfileInput
                        id="newPassword"
                        type="password"
                        placeholder="Enter your new password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>
                      {form.formState.errors.newPassword?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                name="confirmPassword"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <ProfileLabel htmlFor="confirmPassword">
                      Confirm New Password
                      <RequiredLabel />
                    </ProfileLabel>
                    <FormControl>
                      <ProfileInput
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm your new password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>
                      {form.formState.errors.confirmPassword?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />
            </CardContent>

            <CardFooter>
              <div className="flex flex-row justify-end w-full space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => form.reset()}
                  disabled={changePasswordMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={changePasswordMutation.isPending}
                  loadText="Updating..."
                  className="h-10 px-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={!form.formState.isValid}
                >
                  <FloppyDisk className="w-6 h-6 mr-2" />
                  Change Password
                </Button>
              </div>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </div>
  );
}
